import { Head, router } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import PageHeader from '@/Components/PageHeader';
import Alert from '@/Components/Alert';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import { ArrowLeftIcon, CheckIcon, XMarkIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';

function InfoRow({ label, value }) {
    return value ? <div className="py-3 border-b border-slate-100 last:border-0 flex flex-col sm:flex-row sm:justify-between gap-1"><span className="text-sm text-slate-500">{label}</span><span className="text-sm font-medium text-slate-800 text-right">{value}</span></div> : null;
}

export default function Show({ customer, flash }) {
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [approvalNote, setApprovalNote] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [processing, setProcessing] = useState(false);

    const statusBadge = (s) => {
        const map = { approved: 'success', pending: 'warning', rejected: 'danger' };
        return <Badge variant={map[s] || 'neutral'}>{s}</Badge>;
    };

    const isExpiringSoon = (date) => {
        if (!date) return false;
        const expiry = new Date(date);
        const now = new Date();
        const diff = (expiry - now) / (1000 * 60 * 60 * 24);
        return diff <= 30 && diff > 0;
    };

    const isExpired = (date) => date && new Date(date) < new Date();

    const handleApprove = () => {
        setProcessing(true);
        router.patch(`/customers/${customer.id}/status`, {
            status: 'approved',
            approval_note: approvalNote,
        }, {
            onFinish: () => {
                setProcessing(false);
                setShowApproveModal(false);
                setApprovalNote('');
            },
        });
    };

    const handleReject = () => {
        setProcessing(true);
        router.patch(`/customers/${customer.id}/status`, {
            status: 'rejected',
            rejection_reason: rejectionReason,
        }, {
            onFinish: () => {
                setProcessing(false);
                setShowRejectModal(false);
                setRejectionReason('');
            },
        });
    };

    return (
        <AppLayout>
            <Head title={`Customer: ${customer.company_name}`} />
            <PageHeader title={customer.company_name} subtitle="Customer Details" actions={
                <div className="flex gap-2 flex-wrap">
                    <Button href="/customers" variant="outline" size="sm"><ArrowLeftIcon className="w-4 h-4" /> Back</Button>
                    <a href={`/customers/${customer.id}/export-pdf`} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-800 text-white rounded-lg transition-colors"><DocumentArrowDownIcon className="w-4 h-4" /> Export PDF</a>
                    {customer.status === 'pending' && (
                        <>
                            <Button size="sm" onClick={() => setShowApproveModal(true)}><CheckIcon className="w-4 h-4" /> Approve</Button>
                            <Button variant="danger" size="sm" onClick={() => setShowRejectModal(true)}><XMarkIcon className="w-4 h-4" /> Reject</Button>
                        </>
                    )}
                </div>
            } />
            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {flash?.warning && <Alert type="warning" className="mb-4">{flash.warning}</Alert>}
            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center"><h3 className="font-semibold text-slate-800">Company Information</h3>{statusBadge(customer.status)}</div>
                    <CardBody>
                        <InfoRow label="Company Name" value={customer.company_name} />
                        <InfoRow label="Trade License No." value={customer.trade_license_number} />
                        <InfoRow label="Address" value={customer.address} />
                        <InfoRow label="Phone" value={customer.phone} />
                        <InfoRow label="Fax" value={customer.fax} />
                    </CardBody>
                </Card>
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100"><h3 className="font-semibold text-slate-800">Contact Person</h3></div>
                    <CardBody>
                        <InfoRow label="Name" value={customer.contact_person_name} />
                        <InfoRow label="Email" value={customer.contact_person_email} />
                        <InfoRow label="Phone" value={customer.contact_person_phone} />
                        <InfoRow label="Registered" value={new Date(customer.created_at).toLocaleDateString()} />
                    </CardBody>
                </Card>
            </div>

            {/* Documents */}
            {customer.documents && customer.documents.length > 0 && (
                <Card className="mt-6">
                    <div className="px-6 py-4 border-b border-slate-100"><h3 className="font-semibold text-slate-800">Documents</h3></div>
                    <CardBody>
                        <div className="space-y-3">
                            {customer.documents.map((doc, i) => (
                                <a key={i} href={doc.download_url} target="_blank" rel="noopener"
                                   className={`flex items-center justify-between p-4 rounded-xl border transition-colors cursor-pointer group ${isExpired(doc.expiry_date) ? 'border-red-200 bg-red-50/50 hover:border-red-300 hover:bg-red-50' : isExpiringSoon(doc.expiry_date) ? 'border-amber-200 bg-amber-50/50 hover:border-amber-300 hover:bg-amber-50' : 'border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/50'}`}>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isExpired(doc.expiry_date) ? 'bg-red-100 text-red-500' : isExpiringSoon(doc.expiry_date) ? 'bg-amber-100 text-amber-500' : 'bg-slate-100 text-slate-400 group-hover:bg-emerald-100 group-hover:text-emerald-600'}`}>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-slate-800">{doc.type_label || doc.document_type}</p>
                                            <p className="text-xs text-slate-400 truncate">{doc.original_name}</p>
                                            {doc.expiry_date && (
                                                <p className={`text-xs mt-0.5 ${isExpired(doc.expiry_date) ? 'text-red-600 font-medium' : isExpiringSoon(doc.expiry_date) ? 'text-amber-600 font-medium' : 'text-slate-400'}`}>
                                                    {isExpired(doc.expiry_date) ? 'Expired' : 'Expires'} {new Date(doc.expiry_date).toLocaleDateString()}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <span className="text-[11px] text-slate-400">{doc.human_file_size}{doc.extension ? ` \u00b7 ${doc.extension.toUpperCase()}` : ''}</span>
                                        <svg className="w-4 h-4 text-slate-300 group-hover:text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </CardBody>
                </Card>
            )}

            {/* Approve Modal */}
            <Modal
                show={showApproveModal}
                onClose={() => { setShowApproveModal(false); setApprovalNote(''); }}
                title="Approve Customer"
                description="This will send an approval email to the customer"
                icon={<CheckIcon className="w-5 h-5" />}
                iconBg="bg-emerald-100"
                iconColor="text-emerald-700"
            >
                <ModalBody>
                    <div className="space-y-4">
                        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                            <p className="text-sm text-emerald-800">
                                You are about to approve <strong>{customer.company_name}</strong>. An approval email with the registration PDF will be sent to the customer.
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                Approval Note <span className="text-slate-400 font-normal">(optional - will be included in the email)</span>
                            </label>
                            <textarea
                                value={approvalNote}
                                onChange={(e) => setApprovalNote(e.target.value)}
                                rows={4}
                                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 resize-none"
                                placeholder="e.g. Welcome aboard! Your credit limit has been set to AED 50,000. Our sales team will contact you shortly..."
                            />
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => { setShowApproveModal(false); setApprovalNote(''); }} disabled={processing}>Cancel</Button>
                    <Button onClick={handleApprove} disabled={processing}>
                        {processing ? 'Approving...' : 'Approve & Send Email'}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Reject Modal */}
            <Modal
                show={showRejectModal}
                onClose={() => { setShowRejectModal(false); setRejectionReason(''); }}
                title="Reject Customer"
                description="This will send a rejection email to the customer"
                icon={<XMarkIcon className="w-5 h-5" />}
                iconBg="bg-red-100"
                iconColor="text-red-600"
            >
                <ModalBody>
                    <div className="space-y-4">
                        <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                            <p className="text-sm text-red-800">
                                You are about to reject <strong>{customer.company_name}</strong>'s registration. A rejection email will be sent to the customer.
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                Rejection Reason <span className="text-slate-400 font-normal">(optional - will be included in the email)</span>
                            </label>
                            <textarea
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                rows={4}
                                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500/20 resize-none"
                                placeholder="e.g. Missing required documents. Please resubmit with a valid trade license and VAT certificate..."
                            />
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => { setShowRejectModal(false); setRejectionReason(''); }} disabled={processing}>Cancel</Button>
                    <Button variant="danger" onClick={handleReject} disabled={processing}>
                        {processing ? 'Rejecting...' : 'Reject & Send Email'}
                    </Button>
                </ModalFooter>
            </Modal>
        </AppLayout>
    );
}
